import { UserDTO } from '@ytl/common-model';
import { Button, errorIcon, ViewMode } from '@ytl/common-web';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApp } from '../../../contexts/appContext';
import { selectCurrentUser, setCurrentUserData } from '../../../contexts/currentUserSlicer';
import { useLanguage } from '../../../contexts/languageContext';
import { AppRoutes } from '../../../routes';
import { getConfig } from '../../data/config';
import {
  accountSaveErrorDialogProps,
  confirmChangesDialogProps,
  emailSentDialogProps,
  nameSaveSuccessDialogProps,
} from '../../data/dialogConfig';
import { accountResource } from '../../resource/account.resource';
import { authResource } from '../../resource/auth.resource';
import { isValidEmail } from '../../utils/emailValidator';
import { formatPhoneNumberWithSpaces } from '../../utils/phoneNumberFormatter';
import { AccountDataEditor, AccountDataEditorType } from '../custom/account-data-editor/AccountDataEditor';
import { DialogCloseReason } from '../custom/dialog/Dialog';
import { DataCard } from '../ui/data-card/DataCard';

export type AccountSettingsProps = {
  onSetupMFA: () => void;
  onDisconnectMFA: () => void;
  onChangePassword: () => void;
  isB2BMode?: boolean;
};

export const AccountSettings = ({ onChangePassword, isB2BMode = false }: AccountSettingsProps) => {
  const { translation } = useLanguage();
  const dispatch = useDispatch();
  const { openDialog } = useApp();
  const currentUser = useSelector(selectCurrentUser) as UserDTO;
  const [accountName, setAccountName] = useState(currentUser.name);
  const [accountEmail] = useState(currentUser.email);
  const [valueChanges, setValueChanges] = useState({ name: false, email: false, phone: false });
  const [viewModes, setViewModes] = useState<Record<AccountDataEditorType, ViewMode>>({
    name: ViewMode.Text,
    email: ViewMode.View,
    phone: ViewMode.View,
  });

  const saveAccountName = useCallback(
    async (newName: string) => {
      const errors = [];
      if (newName.length === 0) {
        errors.push(translation.accountSettings.accountDataCard.errors.emptyName);
      }
      if (errors.length) {
        return errors;
      }
      return accountResource.updateAccountName(newName, currentUser.accountId);
    },
    [currentUser, translation],
  );

  const saveEmail = useCallback(
    async (newEmail: string) => {
      const errors = [];
      if (newEmail.length === 0) {
        errors.push(translation.accountSettings.accountDataCard.errors.emptyEmail);
      }
      if (!isValidEmail(newEmail)) {
        errors.push(translation.accountSettings.accountDataCard.errors.invalidEmail);
      }
      if (errors.length) {
        return errors;
      }
      return accountResource.updateEmail(newEmail, currentUser.accountId);
    },
    [currentUser, translation],
  );

  const canModify = useCallback(
    async (type: AccountDataEditorType) => {
      const otherEditorType: AccountDataEditorType = type === 'email' ? 'name' : 'email';
      if (viewModes[otherEditorType] === ViewMode.Edit && valueChanges[otherEditorType]) {
        const dialogRes = await openDialog(confirmChangesDialogProps(translation));
        if (dialogRes === DialogCloseReason.Ok) {
          return false;
        }
      }
      setValueChanges({ name: false, email: false, phone: false });
      setViewModes({ ...viewModes, [type]: ViewMode.Edit, [otherEditorType]: ViewMode.View });
      return true;
    },
    [openDialog, translation, valueChanges, viewModes],
  );

  const onCancel = useCallback(
    async (type: AccountDataEditorType) => {
      if (valueChanges[type]) {
        const res = await openDialog(confirmChangesDialogProps(translation));
        if (res !== DialogCloseReason.Cancel) {
          return false;
        }
      }
      setValueChanges({ name: false, email: false, phone: false });
      setViewModes({ ...viewModes, [type]: ViewMode.View });
      return true;
    },
    [openDialog, translation, valueChanges, viewModes],
  );

  const onSave = useCallback(
    async (type: AccountDataEditorType, newValue: string, newValue2?: string) => {
      const saveFn = type === 'email' ? saveEmail : saveAccountName;
      if (type === 'email') {
        if (!isValidEmail(newValue)) {
          return [translation.emailChange.error.invalidEmail];
        }
        if (newValue !== newValue2) {
          return [translation.emailChange.error.emailMismatch];
        }

        if (newValue === accountEmail) {
          setViewModes({ ...viewModes, [type]: ViewMode.View });
          return true;
        }
      } else {
        if (newValue === accountName) {
          setViewModes({ ...viewModes, [type]: ViewMode.View });
          return true;
        }
        if (!newValue.match(/^[\p{Letter}\p{Mark}]+([_\\. \\-\\,][\p{Letter}\p{Mark}]+)*$/u)) {
          return [translation.accountSettings.accountDataCard.nameFormat];
        }
      }

      try {
        const res = await saveFn(newValue);
        if (res instanceof Array) {
          return res;
        }
        if (res) {
          if (type === 'email') {
            accountResource.fullLogout();
            await openDialog(emailSentDialogProps(translation));
            window.location.href = AppRoutes.homepage;
          } else {
            await openDialog(nameSaveSuccessDialogProps(translation));
            setAccountName(newValue);
          }
          setValueChanges({ name: false, email: false, phone: false });
          setViewModes({ ...viewModes, [type]: ViewMode.View });

          const accountData = await authResource.getAccount();
          dispatch(setCurrentUserData(accountData));
          return true;
        } else {
          // unknown error
          throw new Error();
        }
      } catch {
        await openDialog(accountSaveErrorDialogProps(translation));
        return false;
      }
    },
    [accountEmail, accountName, dispatch, openDialog, saveAccountName, saveEmail, translation, viewModes],
  );

  const onChange = useCallback(
    async (type: AccountDataEditorType) => {
      setValueChanges({ ...valueChanges, [type]: true });
    },
    [valueChanges],
  );

  useEffect(() => {
    if (window.location.pathname === AppRoutes.smsLoginError) {
      accountResource.fullLogout().catch(reason => console.error('error during logout', reason));
    }
  }, []);

  useLayoutEffect(() => {
    if (window.location.pathname === AppRoutes.smsLoginError) {
      openDialog({
        ...errorIcon,
        titleHTML: translation.accountSettings.smsLoginError.title,
        content: translation.accountSettings.smsLoginError.content,
        okLabel: translation.accountSettings.smsLoginError.primaryAction,
        cancelLabel: translation.accountSettings.smsLoginError.secondaryAction,
        verticalActions: true,
        cancelButtonVariant: 'text-undecorated',
      }).then(res => {
        if (res === DialogCloseReason.Ok) {
          AppRoutes.goToLogout();
        } else if (res === DialogCloseReason.Cancel) {
          window.location.href = getConfig().mainYettelUrl;
        }
      });
    }
  }, [openDialog, translation]);

  return (
    <section className="account-settings">
      {!isB2BMode && (
        <DataCard header={translation.accountSettings.accountDataCard.header}>
          <div className="account-data account-data--account-name">
            <AccountDataEditor
              label={translation.accountSettings.accountDataCard.nameLabel}
              value={accountName || accountEmail}
              type={'name'}
              viewMode={ViewMode.Text}
              canModify={canModify}
              onSave={onSave}
              canCancel={onCancel}
              onValueChange={onChange}
            />
          </div>

          <div className="account-data account-data--email-address">
            <AccountDataEditor
              label={translation.accountSettings.accountDataCard.emailLabel}
              value={accountEmail}
              type={'email'}
              viewMode={viewModes.email}
              canModify={canModify}
              onSave={onSave}
              canCancel={onCancel}
              onValueChange={onChange}
              showModify={currentUser.category === 'DUP' || currentUser.category === 'YTV'}
            />
          </div>

          {currentUser.category !== 'YTV' && (
            <div className="account-data account-data--phone-number">
              <AccountDataEditor
                label={translation.accountSettings.accountDataCard.phoneLabel}
                value={formatPhoneNumberWithSpaces(currentUser.ctn || '', true)}
                type={'phone'}
                viewMode={viewModes.phone}
                canModify={canModify}
                onSave={onSave}
                canCancel={onCancel}
                onValueChange={onChange}
                showModify={currentUser.category === 'DUP'}
              />
            </div>
          )}
        </DataCard>
      )}

      <DataCard header={translation.accountSettings.passwordDataCard.header}>
        <div className="account-data account-data--password-info">
          <div>
            <legend>{translation.accountSettings.passwordDataCard.passwordLabel}</legend>
            <p>{translation.accountSettings.passwordDataCard.passwordValue}</p>
          </div>
          <div className={'account-data__button-row'}>
            <Button
              onClick={() => onChangePassword()}
              variant={'text'}
              aria-label={`${translation.accountSettings.passwordDataCard.passwordLabel} ${translation.accountSettings.passwordDataCard.modifyButton}`}
              hidden={currentUser.category !== 'DUP' && currentUser.category !== 'YTV'}
            >
              {translation.accountSettings.passwordDataCard.modifyButton}
            </Button>
          </div>
        </div>
      </DataCard>
    </section>
  );
};
